import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  return (
    <main className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <Link 
            to="/" 
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#F58227] transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Title */}
            <div className="mb-12">
              <p className="text-sm font-medium uppercase tracking-widest text-[#0CBCBF] mb-4">
                Legal
              </p>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-[#27272A] mb-6">
                Privacy Policy
              </h1>
              <p className="text-[#A1A1AA]">
                Effective Date: December 2025
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none space-y-8 text-[#52525B]">
              {/* Security Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
                <p className="text-sm text-amber-800 font-medium">
                  Important: For your safety, don't store passwords or sensitive credentials in this app.
                </p>
              </div>

              <p className="text-lg leading-relaxed">
                Where was i ("we", "our", or "the app") respects your privacy and is committed to protecting the information you share with us.
              </p>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Information We Collect
                </h2>
                <p className="leading-relaxed mb-4">
                  We collect only the information necessary to operate the app:
                </p>
                <ul className="space-y-3">
                  <li>Account information such as your email address if you create an account</li>
                  <li>Task and board data that you enter into the application</li>
                  <li>Basic usage data to improve reliability and performance</li>
                </ul>
                <p className="leading-relaxed mt-4 font-medium">
                  We do not sell or rent your personal information to third parties.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  How Your Information Is Used
                </h2>
                <p className="leading-relaxed mb-4">
                  Your information is used solely to:
                </p>
                <ul className="space-y-3">
                  <li>Provide the core functionality of the app</li>
                  <li>Store your tasks and board data between sessions</li>
                  <li>Maintain and improve the reliability of the service</li>
                  <li>Communicate with you if necessary regarding support or account issues</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Data Storage
                </h2>
                <p className="leading-relaxed">
                  Your data is stored on secure cloud infrastructure. We take reasonable steps to protect your information but cannot guarantee absolute security.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Third-Party Services
                </h2>
                <p className="leading-relaxed">
                  Where was i may rely on third-party infrastructure providers such as authentication services, payment processors, or hosting providers.
                </p>
                <p className="leading-relaxed mt-4">
                  These services receive only the information necessary to operate the application.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Your Control Over Your Data
                </h2>
                <p className="leading-relaxed">
                  You may request deletion of your account and associated data by contacting support.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Changes to This Policy
                </h2>
                <p className="leading-relaxed">
                  This policy may be updated periodically. Updates will be posted on this page.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-4">
                  Contact
                </h2>
                <p className="leading-relaxed">
                  For privacy questions please contact:
                </p>
                <p className="leading-relaxed mt-2">
                  <a 
                    href="mailto:support@getwherewasi.com" 
                    className="text-[#0CBCBF] hover:text-[#F58227] transition-colors"
                  >
                    support@getwherewasi.com
                  </a>
                </p>
              </div>

              <div className="bg-[#FCEBDC]/50 rounded-2xl p-8 mt-12">
                <p className="font-handwritten text-xl text-[#F58227] text-center mb-0">
                  "We built this for people like us. Your privacy is personal."
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-16 text-center">
              <Link
                to="/"
                data-testid="privacy-cta-home"
                className="inline-flex items-center gap-2 bg-[#F58227] text-white hover:bg-[#D96D19] rounded-full px-8 py-4 text-lg font-bold shadow-lg hover:shadow-[#F58227]/30 transition-all active:scale-95"
              >
                Back to Home
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
